import {Box, Button, Dialog, DialogActions, DialogContent, Grid, useTheme} from "@mui/material";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import {AgGridReact} from "ag-grid-react";
import dayjs from "dayjs";
import React, {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {useAgGridLocale} from "../../../../config/globalConfig";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import SectionHeader from "../../../../util/component/SectionHeader";
import {useExceptionListRepository} from "../../api/repositories/ExceptionListRepository";
import {useCommercialCategoryRepository} from "../../api/repositories/CommercialCategoryRepository";
import {
    ExceptionListEntityRequest,
    ExceptionListEntityResponse,
    FormExceptionListModel,
    FormSearchExceptionListModel,
} from "../../api/types/exceptionList";
import {DialogInterface, FormDialog, SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import BasicToolbar from "../../common/components/BasicToolbar";
import {
    dateFormatter,
    fromGenericBeToGenericFe,
    fromSingleGenericBeToSingleGenericFe,
    fromSingleGenericFeToSingleGenericBe,
    useActionColDef,
} from "../../utils";
import {yupReservationFromFunc, yupReservationToFunc} from "../../yupUtils";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import {FormProvider, useForm} from "react-hook-form";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import * as yup from "yup";
import BootstrapDialogTitle from "../../common/components/BootstrapDialogTitle";
import FormExceptionList from "./FormExceptionList/FormExceptionList";
import {useCommercialSegmentRepository} from "../../api/repositories/CommercialSegmentRepository";
import {commercialSegmentListAtom} from "../../state/commercialSegmentsAtom";
import ActionsPopover from "../../common/components/ActionsPopover";
import DialogComponent from "../../common/components/DialogComponent";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE} from "../../api/types/commercialCategory";
import {commercialCategoryListAtom} from "../../state/commercialCategoriesRecoil";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";
import ExceptionListFilters from "./Filters/ExceptionListFilters";
import Loading from "../../common/components/Loading";

const ExceptionList = () => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Recoil
    const [, setCommercialCategoryList] = useRecoilState(commercialCategoryListAtom);
    const [, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);

    // Use Repository Hooks
    const {getCommercialSegments} = useCommercialSegmentRepository();
    const {
        getExceptionListsPaged,
        putExceptionList,
        postExceptionList,
        isExceptionListInUse,
        deleteExceptionList,
        terminateExceptionList
    } = useExceptionListRepository();
    const {getCommercialCategories, getCommercialCategoriesByCommercialSegmentId} = useCommercialCategoryRepository();

    // Use State
    const [formDialog, setFormDialog] = useState<FormDialog | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [initialValues] = useState<FormExceptionListModel>({
        commercialCategoryId: null,
        commercialSegmentId: null,
        name: "",
        from: "",
        to: "",
        description: "",
        ancillaryPack: false,
        dealer: null,
        legalEntity: null,
    });
    const [commercialCategoryListOriginal, setCommercialCategoryListOriginal] = useState<CommercialCategorySelectFE[]>([]);
    const [exceptionList, setExceptionList] = useState<ExceptionListEntityResponse | null>(null);

    const handleGetStaticContent = async () => {
        // TODO: togliere COMMERCIAL_CATEGORIES || quando si pusha
        // const commercialCategoriesData: CommercialCategoryEntityResponse[] = COMMERCIAL_CATEGORIES || await getCommercialCategories();
        const commercialCategoriesData: CommercialCategoryEntityResponse[] =
            await getCommercialCategories();

        const commercialCategoryList: CommercialCategorySelectFE[] =
            fromGenericBeToGenericFe(commercialCategoriesData);
        setCommercialCategoryList(commercialCategoryList);
        setCommercialCategoryListOriginal(commercialCategoryList);

        // TODO: togliere COMMERCIAL_SEGMENTS || quando si pusha
        // const commercialSegmentsData = COMMERCIAL_SEGMENTS || await getCommercialSegments();
        const commercialSegmentsData = await getCommercialSegments();

        const commercialSegmentList: SelectElementModelFE[] =
            fromGenericBeToGenericFe(commercialSegmentsData);
        setCommercialSegmentList(commercialSegmentList);
    };

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchExceptionListModel>({
        listName: "",
        commercialCategoryId: null,
        commercialSegmentId: null,
        from: null,
        to: null,
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
    })

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
    }, []);

    /* AG GRID - START */
    // Use Grid Data Custom Hook
    const {setServerSideDatasource, handleTriggerFilters, refresh} =
        useAgGridCustomHook<
            ExceptionListEntityResponse,
            FormSearchExceptionListModel
        >({
            gridRef,
            api: getExceptionListsPaged,
            initialSortColId: "name",
            entityFilters: testFilters,
            customSort: "ExceptionListEntityResponse",
        });

    useEffect(() => {
        if (gridRef?.current) {
            handleTriggerFilters({
                listName: testFilters.listName,
                commercialCategoryId:
                    (
                        testFilters.commercialCategoryId as SelectElementModelFE
                    )?.code?.toString() || "",
                commercialSegmentId:
                    (
                        testFilters.commercialSegmentId as SelectElementModelFE
                    )?.code?.toString() || "",
                from: testFilters.from
                    ? dayjs(testFilters.from).toISOString()
                    : testFilters.from,
                to: testFilters.to
                    ? dayjs(testFilters.to).toISOString()
                    : testFilters.to,
                legalEntities:
                    ((testFilters.legalEntities as SelectElementModelFE)
                        ?.code as string) === "all"
                        ? everyLegalEntitiesWithoutAll
                        : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
                dealers:
                    ((testFilters.dealers as SelectElementModelFE)
                        ?.code as string) === "all"
                        ? dealerFilteredOptions
                        : ((testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)),
            });
        }
    }, []);

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            listName: testFilters.listName,
            commercialCategoryId:
                (
                    testFilters.commercialCategoryId as SelectElementModelFE
                )?.code?.toString() || "",
            commercialSegmentId:
                (
                    testFilters.commercialSegmentId as SelectElementModelFE
                )?.code?.toString() || "",
            from: testFilters.from
                ? dayjs(testFilters.from).toISOString()
                : testFilters.from,
            to: testFilters.to
                ? dayjs(testFilters.to).toISOString()
                : testFilters.to,
            legalEntities:
                ((testFilters.legalEntities as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? everyLegalEntitiesWithoutAll
                    : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
            dealers:
                ((testFilters.dealers as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? dealerFilteredOptions
                    : ((testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)),
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const handleTriggerResetFilters = () => {
        setTestFilters({
            listName: "",
            commercialCategoryId: null,
            commercialSegmentId: null,
            from: null,
            to: null,
            dealers: [],
            legalEntities: []
        });
        handleTriggerFilters({
            listName: "",
            commercialCategoryId: null,
            commercialSegmentId: null,
            from: null,
            to: null,
            dealers: [],
            legalEntities: []
        });
    };

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef[] = [
        {
            field: "legalEntities",
            headerName: t("pricing:exceptionList.table.labels.legalEntities"),
            headerTooltip: t("pricing:exceptionList.table.labels.legalEntitiess"),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.legalEntity)),
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "dealers",
            headerName: t("pricing:exceptionList.table.labels.dealers"),
            headerTooltip: t("pricing:exceptionList.table.labels.dealers"),
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:dealers.'.concat(params.data.dealer)),
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            field: "listName",
            headerName: `${t("pricing:exceptionList.table.labels.name")}`,
            headerTooltip: `${t("pricing:exceptionList.table.labels.name")}`,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams<ExceptionListEntityResponse>) => params.data?.name
        },
        {
            field: "description",
            headerName: `${t("pricing:exceptionList.form.labels.description")}`,
            headerTooltip: `${t("pricing:exceptionList.form.labels.description")}`,
            flex: 1
        },
        {
            field: "commercialSegmentId",
            headerName: t("pricing:exceptionList.form.labels.commercialSegment"),
            headerTooltip: t("ppricing:exceptionList.form.labels.commercialSegment"),
            flex: 1,
            valueFormatter: (params: any) => params.data.commercialCategory.commercialSegment.name,
            filter: "text"
        },
        {
            field: "commercialCategoryId",
            headerName: t("pricing:exceptionList.table.labels.commercialCategory"),
            headerTooltip: t("ppricing:exceptionList.table.labels.commercialCategory"),
            flex: 1,
            valueFormatter: (params: any) => params.data.commercialCategory.name,
            filter: "text"
        },
        {
            field: "from",
            headerName: t("pricing:exceptionList.table.labels.from"),
            headerTooltip: t("pricing:exceptionList.table.labels.from"),
            flex: 1,
            valueFormatter: (params: any) => params.data.from ? dateFormatter(params.data.from) : "",
            filter: "text"

        },
        {
            field: "to",
            headerName: t("pricing:exceptionList.table.labels.to"),
            headerTooltip: t("ppricing:exceptionList.table.labels.to"),
            flex: 1,
            valueFormatter: (params: any) => params.data.to ? dateFormatter(params.data.to) : "",
            filter: "text"
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) => (
                <ActionsPopover
                    params={params}
                    view={{
                        action: viewButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_VIEW_DETAIL_EXCEPTION_LIST"
                            })
                        })
                    }}
                    update={{
                        action: updateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_UPDATE_EXCEPTION_LIST"
                            })
                        })
                    }}
                    terminate={{
                        action: terminateButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_TERMINATE_EXCEPTION_LIST"
                            })
                        })
                    }}
                    del={{
                        action: deleteButtonClicked, userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_DELETE_EXCEPTION_LIST"
                            })
                        })
                    }}
                />
            )
        }

    ];

    const defaultColDef = DEFAULT_COL_DEF;
    /* AG GRID - END */

    /* FORM - START */
    const validationSchema = () => {
        const yupReservationFrom = yupReservationFromFunc({
            t,
            id: exceptionList?.id || null,
            reservationFrom: exceptionList?.from as string,
        });
        const yupReservationTo = yupReservationToFunc({
            t,
            id: exceptionList?.id || null,
            reservationTo: exceptionList?.to as string,
            reservationFromFieldName: "from",
        });

        return yup.object().shape({
            name: yup.string().required(t("translation:validation.fieldMandatory")).min(2),
            dealer: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .required(t("translation:validation.fieldMandatory"))
                .nullable(true),
            legalEntity: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            commercialCategoryId: yup.object().shape({
                code: yup.string(),
                label: yup.string()
            }).required(t("translation:validation.fieldMandatory")).nullable(true),
            commercialSegmentId: yup.object().shape({
                code: yup.string(),
                label: yup.string()
            }).required(t("translation:validation.fieldMandatory")).nullable().required(t("translation:validation.fieldMandatory")),
            description: yup.string().required(t("translation:validation.fieldMandatory")),
            from: yupReservationFrom,
            to: yupReservationTo,
            ancillaryPack: yup.boolean()
        }).defined();
    }

    // Use Form
    const form = useForm<FormExceptionListModel>({
        defaultValues: initialValues,
        resolver: yupResolver(
            validationSchema(),
            {},
            {
                rawValues: true,
            }
        ),
        shouldUnregister: true,
    });
    /* FORM - END */

    /* TABLE ACTIONS - START */
    const handleClickViewExceptionList = async (
        exceptionList: ExceptionListEntityResponse
    ) => {
        const commercialCategory: CommercialCategorySelectFE =
            fromSingleGenericBeToSingleGenericFe(
                exceptionList.commercialCategory
            ) as CommercialCategorySelectFE;
        const commercialSegment: SelectElementModelFE =
            fromSingleGenericBeToSingleGenericFe(
                commercialCategory?.commercialSegment
            ) as SelectElementModelFE;

        if (exceptionList) {
            const result = {
                id: exceptionList.id,
                name: exceptionList.name,
                commercialCategoryId: commercialCategory,
                commercialSegmentId: commercialSegment,
                description: exceptionList.description,
                from: exceptionList.from,
                to: exceptionList.to,
                ancillaryPack: exceptionList.ancillaryPack,
                dealer: {code: 0, label: exceptionList.dealer},
                legalEntity: {code: 0, label: exceptionList.legalEntity},
            };

            const commercialCategoriesData =
                await getCommercialCategoriesByCommercialSegmentId(
                    (commercialSegment as SelectElementModelFE).code as string
                );
            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            setCommercialCategoryList(commercialCategoryList);

            form.reset({...result}); // asynchronously reset your form values
            setFormDialog({
                open: true,
                onClickConfirm: form.handleSubmit(handleConfirmSave),
                readonly: true,
                inUse: false
            });
        }
    };

    const handleClickUpdateExceptionList = async (
        exceptionList: ExceptionListEntityResponse
    ) => {
        const exceptionListIsInUse = await isExceptionListInUse(exceptionList.id);
        const commercialCategory: CommercialCategorySelectFE =
            fromSingleGenericBeToSingleGenericFe(
                exceptionList.commercialCategory
            ) as CommercialCategorySelectFE;
        const commercialSegment: SelectElementModelFE =
            fromSingleGenericBeToSingleGenericFe(
                commercialCategory?.commercialSegment
            ) as SelectElementModelFE;

        if (exceptionList) {
            const result: FormExceptionListModel = {
                id: exceptionList.id,
                name: exceptionList.name,
                commercialCategoryId: commercialCategory,
                commercialSegmentId: commercialSegment,
                description: exceptionList.description,
                from: exceptionList.from,
                to: exceptionList.to,
                ancillaryPack: exceptionList.ancillaryPack,
                dealer: {code: exceptionList.dealer, label: exceptionList.dealer},
                legalEntity: {
                    code: exceptionList.legalEntity,
                    label: exceptionList.legalEntity,
                },
            };
            form.reset({...result});
            form.setValue("id", exceptionList.id);
            setExceptionList(exceptionList)

            setFormDialog({
                open: true,
                onClickConfirm: form.handleSubmit(handleConfirmSave),
                readonly: false,
                inUse: exceptionListIsInUse,
            });
        }
    };

    const handleConfirmSave = async (values: FormExceptionListModel) => {
        const newExceptionList: ExceptionListEntityRequest = {
            commercialCategory: fromSingleGenericFeToSingleGenericBe(values.commercialCategoryId as CommercialCategorySelectFE),
            name: values.name,
            from: values.from,
            to: values.to,
            description: values.description,
            ancillaryPack: values.ancillaryPack,
            dealer: dealerFilteredOptions.length === 1 ? dealerFilteredOptions[0] : values.dealer?.code as string,
            legalEntity: legalEntityOptions.length === 1 ? everyLegalEntitiesWithoutAll[0] : values.legalEntity?.code as string,
        };

        try {
            values?.id
                ? await putExceptionList({
                    id: values?.id,
                    ...JSON.parse(JSON.stringify(newExceptionList)),
                })
                : await postExceptionList(newExceptionList);

            // reset form
            setFormDialog(null);
            setExceptionList(null);
            form.reset(initialValues);
            // get lista ancillary list
            refresh();
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    const handleConfirmDelete = async (id: number) => {
        try {
            if (id) {
                await deleteExceptionList(id);
                setDialog(null);
                await refresh();
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            }
        } catch (e) {
        }
    };

    const viewButtonClicked = (data: ExceptionListEntityResponse) => {
        handleClickViewExceptionList(data);
    };

    const updateButtonClicked = async (data: ExceptionListEntityResponse) => {
        handleClickUpdateExceptionList(data);
    };

    const terminate = async (id: number) => {
        await terminateExceptionList(id);
        setDialog(null);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        // });
    };

    const terminateButtonClicked = async (data: ExceptionListEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => terminate(data.id),
            message: t("pricing:exceptionList.dialogContents.terminateExceptionList"),
        });
    };

    const deleteButtonClicked = (data: ExceptionListEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmDelete(data.id),
            message: t("pricing:exceptionList.dialogContents.removeExceptionList"),
        });
    };
    /* TABLE ACTIONS - END */


    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_EXCEPTION_LIST";
                        });
                    }) ?
                        {
                            onClick: () => setFormDialog({
                                open: true,
                                onClickConfirm: form.handleSubmit(handleConfirmSave),
                                readonly: false,
                                inUse: false
                            })
                        } : undefined}
            />
        )
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_EXCEPTION_LIST.name")}/>

            {/* FORM  */}
            {formDialog?.open &&
                <Dialog open={Boolean(formDialog?.open)} maxWidth={"lg"}
                        fullWidth>
                    <BootstrapDialogTitle id="customized-dialog-title" onClose={() => {
                        // onCloseDialog();
                        form.reset(initialValues);
                        setExceptionList(null);
                        setFormDialog(null);
                    }}>
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            {t("pricing:exceptionList.section.titles.exceptionList")}
                        </Box>
                    </BootstrapDialogTitle>
                    <DialogContent style={{color: theme.palette.primary.main}}>
                        <FormProvider {...form}>
                            <FormExceptionList
                                readOnly={Boolean(formDialog?.readonly)}
                                inUse={Boolean(formDialog?.inUse)}
                                commercialCategoryListOriginal={commercialCategoryListOriginal}
                            />
                        </FormProvider>
                    </DialogContent>
                    {!formDialog?.readonly &&
                        <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                            <Button variant="outlined"
                                    color={"accent"}
                                    onClick={() => {
                                        // cancelDialog()
                                        form.reset(initialValues);
                                        setExceptionList(null);
                                        setFormDialog(null);
                                    }}
                            >
                                {t("pricing:actions.cancel")}
                            </Button>
                            <Button sx={{marginLeft: "16px"}}
                                    autoFocus
                                    color={"accent"}
                                    onClick={formDialog?.onClickConfirm}
                            >
                                {t("pricing:actions.confirm")}
                            </Button>

                        </DialogActions>}
                </Dialog>
            }

            <ExceptionListFilters
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
                commercialCategoryListOriginal={commercialCategoryListOriginal}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        toolbar={toolbar}
                        defaultColDef={defaultColDef}
                        columnDefs={columnDefs}
                        pagination
                        localeText={agGridLocale}
                        tooltipShowDelay={0}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={setServerSideDatasource}
                    />
                    {dialog?.open &&
                        <DialogComponent
                            type={"info"}
                            onClickConfirm={dialog.onClickConfirm}
                            setOpen={() => setDialog(null)}
                            open={dialog.open}
                            message={dialog.message}
                        />
                    }
                </Grid>
            </Grid>

            <Loading/>
        </Box>
    );
};

export default ExceptionList;
